import React, { useState, useMemo } from 'react';
import { X, Smartphone, Users, Search, Check, RefreshCw } from 'lucide-react';

// Multi-select recipient picker (phone numbers + WhatsApp groups) used by WhatsApp config
export function WhatsAppRecipientsPicker({ recipients, setRecipients, pairedPhone, groups, search, setSearch, onRefresh, refreshing }) {
    const [customPhone, setCustomPhone] = useState('');

    const newestSync = useMemo(() => {
        if (!groups.length) return null;
        return Math.max(...groups.map((g) => Number(g.updated_at) || 0));
    }, [groups]);

    const lastSyncedLabel = useMemo(() => {
        if (!newestSync) return null;
        const diff = Math.floor(Date.now() / 1000 - newestSync);
        if (diff < 5) return 'just now';
        if (diff < 60) return `${diff}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        return `${Math.floor(diff / 3600)}h ago`;
    }, [newestSync]);

    // Build the option list: paired phone + groups + any extra phones already saved
    const options = useMemo(() => {
        const out = [];
        const seenValues = new Set();

        if (pairedPhone) {
            out.push({ type: 'phone', value: pairedPhone, label: pairedPhone, subtitle: 'Your paired number' });
            seenValues.add(pairedPhone);
        }
        for (const g of groups) {
            out.push({
                type: 'group',
                value: g.jid,
                label: g.subject || '(no name)',
                subtitle: `${g.member_count || 0} members`,
            });
            seenValues.add(g.jid);
        }
        // surface any saved phones the user typed manually that aren't the paired one
        for (const r of recipients) {
            if (r.type === 'phone' && !seenValues.has(r.value)) {
                out.push({ type: 'phone', value: r.value, label: r.value, subtitle: 'Other number' });
                seenValues.add(r.value);
            }
        }
        return out;
    }, [pairedPhone, groups, recipients]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) =>
            (o.label || '').toLowerCase().includes(q) ||
            (o.value || '').toLowerCase().includes(q) ||
            (o.subtitle || '').toLowerCase().includes(q)
        );
    }, [options, search]);

    const isSelected = (val) => recipients.some((r) => r.value === val);

    const toggle = (opt) => {
        if (isSelected(opt.value)) {
            setRecipients(recipients.filter((r) => r.value !== opt.value));
        } else {
            setRecipients([...recipients, { type: opt.type, value: opt.value, label: opt.label }]);
        }
    };

    const removeRecipient = (val) => setRecipients(recipients.filter((r) => r.value !== val));

    const addCustomPhone = () => {
        const v = customPhone.trim();
        if (!v) return;
        if (recipients.some((r) => r.value === v)) { setCustomPhone(''); return; }
        setRecipients([...recipients, { type: 'phone', value: v, label: v }]);
        setCustomPhone('');
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">Send notifications to</h3>
                    <p className="text-xs text-gray-500 mt-1">
                        Pick your phone number and any WhatsApp groups you want job alerts delivered to. Groups appear once your phone is paired.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={refreshing}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    title={lastSyncedLabel ? `Last synced ${lastSyncedLabel}` : 'Refresh group list'}
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                    {lastSyncedLabel ? `Synced ${lastSyncedLabel}` : 'Refresh'}
                </button>
            </div>

            {/* Selected chips */}
            <div className="flex flex-wrap gap-2 min-h-[2rem]">
                {recipients.length === 0 && (
                    <span className="text-xs text-gray-400 py-1.5">No recipients selected — pick at least one below.</span>
                )}
                {recipients.map((r) => (
                    <span key={r.value} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 bg-blue-50 border border-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {r.type === 'group' ? <Users className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}
                        <span className="max-w-[14rem] truncate">{r.label || r.value}</span>
                        <button
                            type="button"
                            onClick={() => removeRecipient(r.value)}
                            className="ml-0.5 w-4 h-4 rounded-full hover:bg-blue-100 flex items-center justify-center text-blue-500 hover:text-blue-700"
                            aria-label={`Remove ${r.label || r.value}`}
                            title="Remove"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>

            {/* Search + dropdown list */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="relative border-b border-gray-200 bg-gray-50">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search phones, groups, or member count…"
                        className="w-full pl-9 pr-3 py-2 text-sm bg-transparent outline-none placeholder:text-gray-400"
                        aria-label="Search recipients"
                    />
                </div>
                <div className="max-h-72 overflow-y-auto">
                    {filtered.length === 0 ? (
                        <div className="px-4 py-6 text-center text-xs text-gray-400">
                            {options.length === 0
                                ? 'No options yet. Pair WhatsApp above; your number and groups will show here.'
                                : 'No matches for that search.'}
                        </div>
                    ) : (
                        filtered.map((opt) => {
                            const sel = isSelected(opt.value);
                            return (
                                <button
                                    key={opt.value}
                                    onClick={() => toggle(opt)}
                                    type="button"
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${sel ? 'bg-blue-50/50' : ''}`}
                                >
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${sel ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'}`}>
                                        {sel && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                                    </div>
                                    <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${opt.type === 'group' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-600'}`}>
                                        {opt.type === 'group' ? <Users className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-gray-900 truncate">{opt.label}</div>
                                        <div className="text-xs text-gray-500 truncate">{opt.subtitle}</div>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Add custom phone */}
            <div>
                <label htmlFor="wa-add-phone" className="block text-xs font-medium text-gray-600 mb-1">Add another phone number</label>
                <div className="flex gap-2">
                    <input
                        id="wa-add-phone"
                        type="text"
                        value={customPhone}
                        onChange={(e) => setCustomPhone(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomPhone())}
                        placeholder="+1234567890"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                    <button
                        type="button"
                        onClick={addCustomPhone}
                        disabled={!customPhone.trim()}
                        className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Add
                    </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                    Use international format (with country code, e.g. <span className="font-mono">+12025550123</span>).
                </p>
            </div>
        </div>
    );
}
